import type { AxiosResponse } from 'axios';
import apiClient, { getErrorMessage } from './client';

/** Backend `Result<T>` envelope in JSON responses. */
interface ResultEnvelope<T> {
  isSuccess: boolean;
  data?: T;
  error?: string | null;
  errors?: string[] | null;
}

function unwrapSuccess(res: AxiosResponse<unknown>): void {
  const raw = res.data;
  if (raw !== null && typeof raw === 'object' && 'isSuccess' in raw) {
    const e = raw as ResultEnvelope<unknown>;
    if (!e.isSuccess) {
      throw new Error((e.error ?? 'Request failed').toString());
    }
    return;
  }
  throw new Error('Unexpected response shape');
}

function unwrapData<T>(res: AxiosResponse<unknown>): T {
  const raw = res.data;
  if (raw !== null && typeof raw === 'object' && 'isSuccess' in raw) {
    const e = raw as ResultEnvelope<T>;
    if (!e.isSuccess) {
      throw new Error((e.error ?? 'Request failed').toString());
    }
    if (e.data === undefined) {
      throw new Error('Empty response data');
    }
    return e.data;
  }
  return raw as T;
}

async function postVoid(url: string, body?: unknown, params?: Record<string, unknown>): Promise<void> {
  try {
    const res = await apiClient.post(url, body ?? {}, { params });
    unwrapSuccess(res);
  } catch (err: unknown) {
    throw new Error(getErrorMessage(err));
  }
}

function triggerBrowserDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const safe = filename.trim() || 'document';
    const a = document.createElement('a');
    a.href = url;
    a.download = safe;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function fetchOnboardingDocumentBlob(documentId: string): Promise<{ blob: Blob; fileNameHint: string }> {
  const res = await apiClient.get(`/Onboarding/documents/${documentId}/file`, {
    responseType: 'blob',
  });
  const blob = res.data as Blob;
  const ctype = String(res.headers['content-type'] ?? '').toLowerCase();

  if (blob.size > 0 && ctype.includes('application/json')) {
    let msg = 'Download failed';
    try {
      const j = JSON.parse(await blob.text()) as { error?: string };
      if (j.error?.trim()) msg = j.error.trim();
    } catch {
      /* ignore invalid JSON */
    }
    throw new Error(msg);
  }

  let fileNameHint = '';
  const cd = String(res.headers['content-disposition'] ?? '');
  const mStar = /filename\*=UTF-8''([^;]+)|filename\*=(?:"?)([^";]+)("?)/i.exec(cd);
  if (mStar) {
    try {
      fileNameHint = decodeURIComponent((mStar[1] || mStar[2] || '').trim());
    } catch {
      fileNameHint = (mStar[1] || mStar[2] || '').trim();
    }
  } else {
    const mPlain = /filename=(?:"([^"]+)"|([^;\s]+))/i.exec(cd);
    fileNameHint = (mPlain?.[1] || mPlain?.[2] || '').trim();
  }

  return { blob, fileNameHint };
}

export const OnboardingStatus = {
  Draft: 'DRAFT',
  Submitted: 'SUBMITTED',
  InReview: 'IN_REVIEW',
  AdditionalInfoRequired: 'ADDITIONAL_INFO_REQUIRED',
  Approved: 'APPROVED',
  Rejected: 'REJECTED',
} as const;

export type OnboardingStatusType =
  | (typeof OnboardingStatus)[keyof typeof OnboardingStatus]
  /** Allow unknown values from API forward-compatibility */
  | string;

/** Same codes as client portal onboarding API. */
export const OnboardingDocTypeCodes = {
  CertificateOfIncorporation: 'CERTIFICATE_OF_INCORPORATION',
  BusinessRegistration: 'BUSINESS_REGISTRATION',
  ProofOfBusinessAddress: 'PROOF_OF_BUSINESS_ADDRESS',
  ArticlesOfAssociation: 'ARTICLES_OF_ASSOCIATION',
  BusinessProofActivity: 'BUSINESS_PROOF_ACTIVITY',
  DirectorId: 'DIRECTOR_ID',
  UboId: 'UBO_ID',
  AddressProofDirector: 'ADDRESS_PROOF_DIRECTOR',
  AddressProofShareholder: 'ADDRESS_PROOF_SHAREHOLDER',
  ProofOfAddress: 'PROOF_OF_ADDRESS',
  Other: 'OTHER',
  /** Admin-only internal files; not returned to client portal. */
  Ledger: 'LEDGER',
} as const;

export const OnboardingPersonRoleCodes = {
  Director: 'DIRECTOR',
  Ubo: 'UBO',
} as const;

export interface UpsertOnboardingCompanyPayload {
  companyName: string;
  registrationNumber: string;
  countryOfIncorporation: string;
  businessActivity: string;
  website?: string | null;
}

export interface AddOnboardingPersonPayload {
  role: string;
  fullName: string;
  nationality: string;
  idNumber: string;
}

export interface UpdateOnboardingPersonPayload {
  role: string;
  fullName: string;
  nationality: string;
  idNumber: string;
}

/** Matches backend `OnboardingApplicationDto` (camelCase). */
export interface AdminOnboardingApplicationDto {
  id: string;
  clientId: string;
  userId: string;
  /** Portal USER ID (XC-…), same as client settings */
  userPublicId?: string | null;
  /** Company legal name or registrant name from profile */
  legalName?: string | null;
  applicationType: string;
  status: string;
  submittedAt?: string | null;
  reviewedAt?: string | null;
  reviewerUserId?: string | null;
  additionalInfoComment?: string | null;
  rejectionReason?: string | null;
  /** Admin-only; omitted for portal clients. */
  adminInternalComment?: string | null;
  extendedQuestionnaireJson?: string | null;
  directorVerificationJson?: string | null;
}

export interface AdminOnboardingCompanyDto {
  id: string;
  applicationId?: string;
  companyName: string;
  registrationNumber: string;
  countryOfIncorporation: string;
  businessActivity: string;
  website?: string | null;
}

export interface AdminOnboardingPersonDto {
  id: string;
  applicationId?: string;
  role: string;
  fullName: string;
  nationality: string;
  idNumber: string;
  verificationStatus: string;
}

export interface AdminOnboardingDocumentDto {
  id: string;
  applicationId?: string;
  personId?: string | null;
  documentType: string;
  originalFileName: string;
  storageKey?: string;
  mimeType?: string | null;
  fileSize: number;
  uploadedByUserId?: string;
}

export interface AdminOnboardingStatusHistoryDto {
  id: string;
  applicationId: string;
  fromStatus?: string | null;
  toStatus: string;
  changedByUserId?: string | null;
  comment?: string | null;
  createdAt: string;
}

export interface AdminOnboardingLinkedSupportTicketDto {
  id: string;
  status: string;
  reportType: string;
  createdAt: string;
}

/** Snapshots when an admin saves the internal note (newest-first in API). */
export interface AdminOnboardingAdminInternalCommentHistoryDto {
  id: string;
  applicationId: string;
  comment?: string | null;
  createdAt: string;
  createdByUserId?: string | null;
}

export interface AdminOnboardingAdminReminderDto {
  id: string;
  onboardingApplicationId: string;
  note: string;
  remindAt: string;
  createdAt: string;
  notificationSentAt?: string | null;
}

/** Matches backend `OnboardingDetailsDto`. */
export interface AdminOnboardingDetailsDto {
  application: AdminOnboardingApplicationDto;
  companyInfo?: AdminOnboardingCompanyDto | null;
  persons: AdminOnboardingPersonDto[];
  documents: AdminOnboardingDocumentDto[];
  statusHistory?: AdminOnboardingStatusHistoryDto[];
  adminInternalCommentHistory?: AdminOnboardingAdminInternalCommentHistoryDto[];
  adminReminders?: AdminOnboardingAdminReminderDto[];
  linkedSupportTickets?: AdminOnboardingLinkedSupportTicketDto[];
}

export const onboardingApi = {
  getByStatus: async (status?: string): Promise<AdminOnboardingApplicationDto[]> => {
    try {
      const res = await apiClient.get(`/Onboarding/by-status`, {
        params: status ? { status } : {},
      });
      return unwrapData<AdminOnboardingApplicationDto[]>(res);
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err));
    }
  },

  getById: async (id: string): Promise<AdminOnboardingApplicationDto> => {
    try {
      const res = await apiClient.get(`/Onboarding/${id}`);
      return unwrapData<AdminOnboardingApplicationDto>(res);
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err));
    }
  },

  getDetails: async (applicationId: string): Promise<AdminOnboardingDetailsDto> => {
    try {
      const res = await apiClient.get(`/Onboarding/details`, { params: { applicationId } });
      return unwrapData<AdminOnboardingDetailsDto>(res);
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err));
    }
  },

  updateAdminInternalComment: async (applicationId: string, comment: string | null): Promise<void> => {
    try {
      const res = await apiClient.put(`/Onboarding/admin-internal-comment`, { comment }, { params: { applicationId } });
      unwrapSuccess(res);
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err));
    }
  },

  addReminder: async (applicationId: string, payload: { note: string; remindAt: string }): Promise<string> => {
    try {
      const res = await apiClient.post(`/Onboarding/reminders`, payload, { params: { applicationId } });
      return unwrapData<string>(res);
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err));
    }
  },

  deleteReminder: async (applicationId: string, reminderId: string): Promise<void> => {
    try {
      const res = await apiClient.delete(`/Onboarding/reminders/${reminderId}`, { params: { applicationId } });
      unwrapSuccess(res);
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err));
    }
  },

  startReview: (applicationId: string) =>
    postVoid(`/Onboarding/start-review`, undefined, { applicationId }),

  requestAdditionalInfo: (applicationId: string, comment: string) =>
    postVoid(`/Onboarding/request-additional-info`, { comment }, { applicationId }),

  approve: (applicationId: string) =>
    postVoid(`/Onboarding/approve`, {}, { applicationId }),

  reject: (applicationId: string, reason: string) =>
    postVoid(`/Onboarding/reject`, { reason }, { applicationId }),

  /** Creates a linked support ticket when status is ADDITIONAL_INFO_REQUIRED but none exists yet (admin). */
  ensureAdditionalInfoTicket: async (applicationId: string): Promise<string> => {
    try {
      const res = await apiClient.post(`/Onboarding/ensure-additional-info-ticket`, {}, { params: { applicationId } });
      return unwrapData<string>(res);
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err));
    }
  },

  /** Opens a browser download via authenticated GET (JWT in header). */
  downloadDocument: async (documentId: string, fallbackFileName: string): Promise<void> => {
    try {
      const { blob, fileNameHint } = await fetchOnboardingDocumentBlob(documentId);
      const name =
        (fileNameHint && fileNameHint.length > 0 ? fileNameHint : null) ||
        (fallbackFileName && fallbackFileName.trim().length > 0 ? fallbackFileName.trim() : null) ||
        'document';
      triggerBrowserDownload(blob, name);
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err));
    }
  },

  /** Same routes as client portal — allowed when caller is admin (see backend `CanMutateDraftContent`). */
  saveExtendedQuestionnaire: async (applicationId: string, json: string, updatedByUserId: string): Promise<void> => {
    try {
      const res = await apiClient.put(
        `/Onboarding/extended-questionnaire`,
        { json },
        { params: { applicationId, updatedBy: updatedByUserId } },
      );
      unwrapSuccess(res);
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err));
    }
  },

  completeDirectorVerification: async (
    applicationId: string,
    body: {
      provider?: string | null;
      sessionId?: string | null;
      reportSummary?: string | null;
      rawPayload?: unknown;
    },
    updatedByUserId: string,
  ): Promise<void> => {
    try {
      const res = await apiClient.post(`/Onboarding/director-verification/complete`, body ?? {}, {
        params: { applicationId, updatedBy: updatedByUserId },
      });
      unwrapSuccess(res);
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err));
    }
  },

  upsertCompany: async (
    applicationId: string,
    payload: UpsertOnboardingCompanyPayload,
    updatedByUserId: string,
  ): Promise<void> => {
    try {
      const res = await apiClient.put(`/Onboarding/add-company`, payload, {
        params: { applicationId, updatedBy: updatedByUserId },
      });
      unwrapSuccess(res);
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err));
    }
  },

  addPerson: async (
    applicationId: string,
    payload: AddOnboardingPersonPayload,
    createdByUserId: string,
  ): Promise<string> => {
    try {
      const res = await apiClient.post(`/Onboarding/add-persons`, payload, {
        params: { applicationId, createdBy: createdByUserId },
      });
      return unwrapData<string>(res);
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err));
    }
  },

  updatePerson: async (
    personId: string,
    payload: UpdateOnboardingPersonPayload,
    updatedByUserId: string,
  ): Promise<void> => {
    try {
      const res = await apiClient.put(`/Onboarding/update-person`, payload, {
        params: { personId, updatedBy: updatedByUserId },
      });
      unwrapSuccess(res);
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err));
    }
  },

  deletePerson: async (personId: string): Promise<void> => {
    try {
      const res = await apiClient.delete(`/Onboarding/delete-person`, { params: { personId } });
      unwrapSuccess(res);
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err));
    }
  },

  uploadDocument: async (params: {
    applicationId: string;
    documentType: string;
    personId?: string | null;
    file: File;
    auditUserId: string;
  }): Promise<string> => {
    try {
      const form = new FormData();
      form.append('file', params.file);
      const res = await apiClient.post(`/Onboarding/documents/upload`, form, {
        params: {
          applicationId: params.applicationId,
          documentType: params.documentType,
          ...(params.personId ? { personId: params.personId } : {}),
          updatedBy: params.auditUserId,
        },
        transformRequest: (data, headers) => {
          if (data instanceof FormData) {
            delete headers['Content-Type'];
          }
          return data;
        },
      });
      return unwrapData<string>(res);
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err));
    }
  },

  deleteDocument: async (documentId: string): Promise<void> => {
    try {
      const res = await apiClient.delete(`/Onboarding/documents/${documentId}`);
      unwrapSuccess(res);
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err));
    }
  },

  submit: async (
    applicationId: string,
    changedByUserId: string,
    comment?: string | null,
    updatedByUserId?: string,
  ): Promise<void> => {
    try {
      const updatedBy = updatedByUserId ?? changedByUserId;
      const res = await apiClient.post(
        `/Onboarding/submit`,
        { comment: comment ?? null },
        { params: { applicationId, changedByUserId, updatedBy } },
      );
      unwrapSuccess(res);
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err));
    }
  },
};
